// Лентата за сигнал над съдържанието — по ЕДНА на екран (останалото е значка
// или точка). Три тежести (docs/inventory.md §Плътност и сигнали):
//  • critical — червено: лява черта 3px `danger`, фон `danger-tint`, бял ⚠ на
//    `danger`; ТЕКСТЪТ е `text-ink` (червен текст върху червения фон не стига
//    контраста). Само за незавършена работа, която държи наличностите или
//    парите грешни;
//  • attention — меко кехлибарено (`brand-tint`), тъмен текст;
//  • info — синьо (`info-tint`): факт, не задача.
// Всеки ред е текст + действие („Продължи"), не само цвят; показват се до
// `limit` реда (по подразбиране 3) — останалите са в кутиите/списъка.
import type { ReactNode } from "react";
import { ExclamationOutlined, InfoOutlined } from "@ant-design/icons";

export type AttentionBannerTone = "critical" | "attention" | "info";

export type AttentionBannerItem = {
  key: string;
  text: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
};

type Props = {
  tone: AttentionBannerTone;
  // надписът за екранния четец (и видим, ако е подаден `title`)
  label: string;
  title?: ReactNode;
  items: AttentionBannerItem[];
  limit?: number;
};

const FRAME_CLASS: Record<AttentionBannerTone, string> = {
  critical: "border-danger/40 border-l-danger bg-danger-tint",
  attention: "border-brand-tint2 border-l-brand bg-brand-tint",
  info: "border-info-tint2 border-l-info bg-info-tint",
};

const ICON_CLASS: Record<AttentionBannerTone, string> = {
  critical: "bg-danger text-white",
  attention: "bg-brand-tint2 text-brand-deep-ink",
  info: "bg-info-tint2 text-info-deep",
};

const ACTION_CLASS =
  "inline-flex min-h-[26px] shrink-0 cursor-pointer items-center whitespace-nowrap rounded-[7px] border border-shade-3 bg-surface px-2.5 text-[12.5px] font-medium leading-[normal] text-ink outline-none hover:border-shade-5 focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] pointer-coarse:min-h-9";

export const AttentionBanner = ({ tone, label, title, items, limit = 3 }: Props) => {
  const shown = items.slice(0, limit);
  if (!shown.length) return null;
  return (
    <section
      aria-label={label}
      data-attention-banner={tone}
      className={`flex gap-3 rounded-xl border border-l-[3px] px-3.5 py-2.5 ${FRAME_CLASS[tone]}`}
    >
      <span
        aria-hidden
        className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${ICON_CLASS[tone]}`}
      >
        {tone === "info" ? <InfoOutlined /> : <ExclamationOutlined />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {title && <span className="text-[13.5px] font-semibold leading-5 text-ink">{title}</span>}
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {shown.map((item) => (
            <li key={item.key} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="min-w-0 flex-1 text-[13px] leading-5 text-ink">{item.text}</span>
              {item.actionLabel && item.onAction && (
                <button type="button" onClick={item.onAction} className={ACTION_CLASS}>
                  {item.actionLabel}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default AttentionBanner;
